import { useMemo, useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Calculator, Plus, Trash, FloppyDisk, FileXls, Download } from "@phosphor-icons/react";
import api from "../../lib/api";
import {
  emi as emiFn,
  remainingTenure,
  schedule,
  scheduleStepUp,
  expandPrepayments,
  formatINR,
  formatTenure,
} from "../../lib/loanMath";

const FREQS = [
  { v: "one_time", label: "One-time" },
  { v: "yearly", label: "Every year" },
  { v: "monthly", label: "Every month" },
];
const QUICK_CHIPS = [50000, 100000, 200000, 500000];
const emptyLine = () => ({
  _lk: `pp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  amount: 100000,
  month: 1,
  frequency: "one_time",
  times: 1,
});

export default function PrepaymentSimulator({ loans, onApplied, prefill, forceOpen }) {
  const [open, setOpen] = useState(!!forceOpen);
  const [loanIdx, setLoanIdx] = useState(0);
  const [reduceMode, setReduceMode] = useState("reduce_tenure");
  const [stepUpPct, setStepUpPct] = useState(0);
  const [lines, setLines] = useState([emptyLine()]);
  const [scenarios, setScenarios] = useState([]);
  const [scenarioName, setScenarioName] = useState("");

  const loan = (loans || [])[loanIdx];
  useEffect(() => { if (!loan && loans?.length) setLoanIdx(0); }, [loan, loans]);

  // Open + autofill when parent passes a prefill (e.g. from Rate Change nudge)
  useEffect(() => {
    if (!prefill) return;
    setOpen(true);
    if (prefill.loan_name) {
      const idx = (loans || []).findIndex((l) => l.name === prefill.loan_name);
      if (idx >= 0) setLoanIdx(idx);
    }
    if (prefill.amount) {
      setLines([{ ...emptyLine(), amount: Number(prefill.amount) }]);
    }
  }, [prefill, loans]);

  // --- base loan numbers ---
  const base = useMemo(() => {
    if (!loan) return null;
    const bal = Number(loan.current_balance) || Number(loan.principal) || 0;
    const rate = Number(loan.interest_rate_pct) || 0;
    const months = Number(loan.remaining_tenure_months) || Number(loan.tenure_years) * 12 || 0;
    const emi = Number(loan.current_emi) || emiFn(bal, rate, months) || 0;
    const charge = Number(loan.prepayment_charge_pct) || 0;
    return { bal, rate, months, emi, charge };
  }, [loan]);

  // --- compute before / after ---
  const sim = useMemo(() => {
    if (!base || base.bal <= 0 || base.emi <= 0) return null;
    const before = schedule(base.bal, base.rate, base.emi);
    const prepays = expandPrepayments(lines, base.months * 2);
    const totalPrepay = prepays.reduce((s, p) => s + p.amount, 0);
    const chargeAmt = (totalPrepay * base.charge) / 100;

    let after;
    let newEmi = base.emi;
    if (reduceMode === "reduce_emi" && prepays.length === 1 && prepays[0].month === 1) {
      // Single lump at month 1: recompute EMI on reduced balance over original tenure
      const newBal = Math.max(base.bal - prepays[0].amount, 0);
      if (newBal <= 0) {
        after = { rows: [], total_interest: 0, months: 0 };
        newEmi = 0;
      } else {
        newEmi = emiFn(newBal, base.rate, base.months);
        after = scheduleStepUp(newBal, base.rate, newEmi, stepUpPct, []);
      }
    } else {
      after = scheduleStepUp(base.bal, base.rate, base.emi, stepUpPct, prepays);
    }

    const interestSaved = before.total_interest - after.total_interest;
    const netSaving = interestSaved - chargeAmt;
    const monthsSaved = before.months - after.months;
    const isClosed = after.months === 0 || after.months === 1 && totalPrepay >= base.bal;

    const makeDate = (n) => {
      const d = new Date();
      d.setMonth(d.getMonth() + n);
      return d.toLocaleDateString("en-IN", { month: "short", year: "numeric" });
    };

    return {
      before, after,
      newEmi, chargeAmt,
      interestSaved, netSaving, monthsSaved,
      isClosed,
      totalPrepay,
      beforeClosure: makeDate(before.months),
      afterClosure: makeDate(after.months),
    };
  }, [base, lines, reduceMode, stepUpPct]);

  const updateLine = (i, patch) =>
    setLines((ls) => ls.map((l, j) => (i === j ? { ...l, ...patch } : l)));
  const addLine = () => setLines((ls) => [...ls, emptyLine()]);
  const removeLine = (i) => setLines((ls) => ls.filter((_, j) => j !== i));

  // --- smart warnings ---
  const warnings = useMemo(() => {
    const w = [];
    if (!loan) return w;
    const lockIso = loan.lock_in_end_date;
    if (lockIso) {
      try {
        const lock = new Date(lockIso);
        if (lock > new Date()) {
          w.push(`This loan is locked-in until ${lock.toLocaleDateString("en-IN")}; prepayment is not allowed yet.`);
        }
      } catch { /* ignore invalid date */ }
    }
    const minPp = Number(loan.min_prepayment_amount) || 0;
    const amt = Number(lines[0]?.amount) || 0;
    if (minPp > 0 && amt < minPp && amt < (base?.bal || 0)) {
      w.push(`Minimum prepayment for this loan is ${formatINR(minPp)}.`);
    }
    if (sim && base && lines[0]?.amount >= base.bal && lines[0]?.frequency === "one_time") {
      const totalWithCharge = base.bal + (base.bal * base.charge / 100);
      w.push(`This will close the loan fully. You need ${formatINR(totalWithCharge)} (including charge ${formatINR(base.bal * base.charge / 100)}).`);
    }
    return w;
  }, [loan, lines, base, sim]);

  // --- compare both options ---
  const bothCompare = useMemo(() => {
    if (!base || !lines[0] || lines[0].frequency !== "one_time") return null;
    const amt = Number(lines[0].amount);
    if (amt <= 0 || amt >= base.bal) return null;
    const prepays = [{ month: lines[0].month || 1, amount: amt }];
    const beforeInt = schedule(base.bal, base.rate, base.emi).total_interest;
    const redTenure = scheduleStepUp(base.bal, base.rate, base.emi, 0, prepays);
    const newBal = base.bal - amt;
    const redEmi = emiFn(newBal, base.rate, base.months);
    const emiSaved = base.emi - redEmi;
    const tenureSaved = beforeInt - redTenure.total_interest;
    const emiModeSchedule = schedule(newBal, base.rate, redEmi);
    const emiModeIntSaved = beforeInt - emiModeSchedule.total_interest;
    return {
      tenure_more: tenureSaved - emiModeIntSaved,
      emi_lower: emiSaved,
    };
  }, [base, lines]);

  // --- scenarios ---
  const loadScenarios = useCallback(async () => {
    try {
      const { data } = await api.get("/loans/scenarios");
      setScenarios(data || []);
    } catch { /* non-fatal */ }
  }, []);
  useEffect(() => { if (open) loadScenarios(); }, [open, loadScenarios]);

  const saveScenario = async () => {
    const name = (scenarioName || "").trim();
    if (!name) return toast.error("Please name the scenario first.");
    if (!sim || !loan) return;
    try {
      await api.post("/loans/scenarios/save", {
        name,
        loan_name: loan.name,
        inputs: { lines, reduceMode, stepUpPct, loan_name: loan.name },
        summary: {
          before_emi: base.emi, after_emi: sim.newEmi,
          before_months: sim.before.months, after_months: sim.after.months,
          interest_saved: sim.interestSaved, net_saving: sim.netSaving,
          charge: sim.chargeAmt, total_prepay: sim.totalPrepay,
          closure_before: sim.beforeClosure, closure_after: sim.afterClosure,
        },
      });
      toast.success(`Saved "${name}"`);
      setScenarioName("");
      await loadScenarios();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const deleteScenario = async (sid) => {
    if (!window.confirm("Delete this scenario?")) return;
    try {
      await api.delete(`/loans/scenarios/${sid}`);
      await loadScenarios();
      toast.success("Deleted");
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const applyToLoan = async () => {
    if (!sim || !loan) return;
    const ok = window.confirm(
      `Apply ${formatINR(sim.totalPrepay)} prepayment to ${loan.name}? This will update its outstanding balance.`
    );
    if (!ok) return;
    toast.success(`Applied — ${loan.name} outstanding updated locally. Click "Save plan" to persist.`);
    onApplied?.({ loan_name: loan.name, prepayment: sim.totalPrepay });
  };

  const downloadExcel = async () => {
    if (!sim || !loan) return;
    try {
      const { data } = await api.post(
        "/loans/simulator-amortisation.xlsx",
        { loan_name: loan.name, rows: sim.after.rows },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url; a.download = `${loan.name.replace(/\s/g, "_")}_amortisation.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="btn-ghost px-3 py-2 text-xs inline-flex items-center gap-1.5 border hairline rounded-md"
        data-testid="prepay-sim-open"
      >
        <Calculator size={12} weight="duotone" /> Prepayment Simulator
      </button>
    );
  }

  if (!loans?.length) {
    return (
      <section className="rounded-lg border hairline p-5 mt-6 bg-card">
        <div className="text-sm text-muted-foreground">Add a loan first to use the Simulator.</div>
      </section>
    );
  }

  return (
    <section
      id="prepay-sim-card"
      className="rounded-lg border hairline p-5 mt-6 bg-card space-y-5"
      data-testid="prepay-sim-section"
    >
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="font-serif text-xl flex items-center gap-2">
            <Calculator size={20} weight="duotone" className="text-[hsl(var(--primary))]" />
            Manual Prepayment Simulator
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Try any amount, any month, any pattern. Numbers update live.
          </p>
        </div>
        {!forceOpen && (
          <button onClick={() => setOpen(false)} className="btn-ghost text-xs px-3 py-1.5" data-testid="prepay-sim-close">Hide</button>
        )}
      </header>

      {/* Loan picker */}
      <div className="grid md:grid-cols-2 gap-3">
        <label className="block">
          <span className="text-xs text-muted-foreground uppercase tracking-wide">Loan</span>
          <select
            value={loanIdx}
            onChange={(e) => setLoanIdx(Number(e.target.value))}
            className="mt-1 w-full bg-background border hairline rounded-md px-3 py-2 text-sm"
            data-testid="prepay-sim-loan"
          >
            {loans.map((l, i) => (
              <option key={l.id || i} value={i}>
                {l.name} · {formatINR(Number(l.current_balance) || Number(l.principal) || 0, true)} · {Number(l.interest_rate_pct).toFixed(2)}%
              </option>
            ))}
          </select>
          {base && (
            <div className="text-[11px] text-muted-foreground mt-1">
              Outstanding {formatINR(base.bal)} · EMI {formatINR(base.emi)} · {formatTenure(base.months)} left
            </div>
          )}
        </label>
        <div className="flex gap-2 items-end flex-wrap">
          <label className="block flex-1 min-w-[180px]">
            <span className="text-xs text-muted-foreground uppercase tracking-wide">After prepayment</span>
            <select
              value={reduceMode}
              onChange={(e) => setReduceMode(e.target.value)}
              className="mt-1 w-full bg-background border hairline rounded-md px-3 py-2 text-sm"
              data-testid="prepay-sim-mode"
            >
              <option value="reduce_tenure">Reduce tenure (keep EMI)</option>
              <option value="reduce_emi">Reduce EMI (keep tenure)</option>
            </select>
          </label>
          <label className="block w-28">
            <span className="text-xs text-muted-foreground uppercase tracking-wide">Step-up %</span>
            <input
              type="number"
              min={0}
              max={20}
              value={stepUpPct}
              onChange={(e) => setStepUpPct(Number(e.target.value))}
              className="mt-1 w-full bg-background border hairline rounded-md px-3 py-2 text-sm"
              data-testid="prepay-sim-stepup"
            />
          </label>
        </div>
      </div>

      {/* Prepayment lines */}
      <div className="space-y-3" data-testid="prepay-sim-lines">
        {lines.map((ln, i) => (
          <div key={ln._lk || `pp-${i}`} className="rounded-md border hairline p-3 bg-[hsl(var(--muted))]/20 space-y-2">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 items-end">
              <label className="col-span-2 sm:col-span-2 block">
                <span className="text-[10px] text-muted-foreground uppercase">Amount (₹)</span>
                <input
                  type="number"
                  value={ln.amount}
                  onChange={(e) => updateLine(i, { amount: Number(e.target.value) })}
                  className="mt-1 w-full bg-background border hairline rounded px-2 py-1.5 text-sm font-mono"
                  data-testid={`prepay-sim-amount-${i}`}
                />
                {base && (
                  <input
                    type="range"
                    min={0}
                    max={base.bal}
                    step={10000}
                    value={ln.amount}
                    onChange={(e) => updateLine(i, { amount: Number(e.target.value) })}
                    className="w-full mt-1 accent-[hsl(var(--primary))]"
                    data-testid={`prepay-sim-slider-${i}`}
                  />
                )}
              </label>
              <label className="block">
                <span className="text-[10px] text-muted-foreground uppercase">Start month</span>
                <input
                  type="number"
                  min={1}
                  value={ln.month}
                  onChange={(e) => updateLine(i, { month: Number(e.target.value) })}
                  className="mt-1 w-full bg-background border hairline rounded px-2 py-1.5 text-sm"
                  data-testid={`prepay-sim-month-${i}`}
                />
              </label>
              <label className="block">
                <span className="text-[10px] text-muted-foreground uppercase">Frequency</span>
                <select
                  value={ln.frequency}
                  onChange={(e) => updateLine(i, { frequency: e.target.value, times: 1 })}
                  className="mt-1 w-full bg-background border hairline rounded px-2 py-1.5 text-sm"
                  data-testid={`prepay-sim-freq-${i}`}
                >
                  {FREQS.map((f) => <option key={f.v} value={f.v}>{f.label}</option>)}
                </select>
              </label>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {QUICK_CHIPS.map((v) => (
                <button
                  key={v} onClick={() => updateLine(i, { amount: v })}
                  className="text-[11px] px-2 py-0.5 rounded-full border hairline hover:bg-[hsl(var(--muted))]/60"
                  data-testid={`prepay-sim-chip-${v}-${i}`}
                >
                  {formatINR(v, true)}
                </button>
              ))}
              {base && (
                <button
                  onClick={() => updateLine(i, { amount: base.bal })}
                  className="text-[11px] px-2 py-0.5 rounded-full border hairline hover:bg-[hsl(var(--muted))]/60"
                  data-testid={`prepay-sim-close-fully-${i}`}
                >Close fully</button>
              )}
              {ln.frequency !== "one_time" && (
                <label className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                  Times:
                  <input
                    type="number" min={1} max={ln.frequency === "monthly" ? 240 : 30}
                    value={ln.times}
                    onChange={(e) => updateLine(i, { times: Number(e.target.value) })}
                    className="w-14 bg-background border hairline rounded px-1 py-0.5 text-xs"
                    data-testid={`prepay-sim-times-${i}`}
                  />
                </label>
              )}
              {lines.length > 1 && (
                <button
                  onClick={() => removeLine(i)}
                  className="ml-auto text-muted-foreground hover:text-destructive text-[11px] inline-flex items-center gap-1"
                  data-testid={`prepay-sim-remove-${i}`}
                >
                  <Trash size={11} /> Remove
                </button>
              )}
            </div>
          </div>
        ))}
        <button
          onClick={addLine}
          className="btn-ghost text-xs px-3 py-1.5 border hairline rounded-md inline-flex items-center gap-1"
          data-testid="prepay-sim-add-line"
        >
          <Plus size={11} /> Add another prepayment
        </button>
      </div>

      {/* Warnings */}
      {warnings.length > 0 && (
        <div className="rounded-md border hairline p-3 bg-amber-500/5 border-amber-500/30 text-xs text-amber-700 space-y-1" data-testid="prepay-sim-warnings">
          {warnings.map((w) => <div key={w}>• {w}</div>)}
        </div>
      )}

      {/* Before / After */}
      {sim && (
        <div className="grid md:grid-cols-2 gap-4" data-testid="prepay-sim-cards">
          <Card title="BEFORE" testid="prepay-before">
            <Row k="Monthly EMI" v={formatINR(base.emi)} />
            <Row k="Remaining tenure" v={formatTenure(base.months)} />
            <Row k="Total interest left" v={formatINR(sim.before.total_interest, true)} />
            <Row k="Closure date" v={sim.beforeClosure} />
          </Card>
          <Card title="AFTER" highlight testid="prepay-after">
            <Row
              k="Monthly EMI"
              v={sim.isClosed ? "₹0 · freed" : formatINR(sim.newEmi)}
              delta={!sim.isClosed && sim.newEmi !== base.emi
                ? `${sim.newEmi > base.emi ? "+" : "−"}${formatINR(Math.abs(sim.newEmi - base.emi))}`
                : ""}
            />
            <Row
              k="Remaining tenure"
              v={sim.isClosed ? "Closed" : formatTenure(sim.after.months)}
              delta={sim.monthsSaved > 0 ? `${sim.monthsSaved} months saved` : ""}
            />
            <Row
              k="Total interest left"
              v={formatINR(sim.after.total_interest, true)}
              delta={sim.interestSaved > 0 ? `${formatINR(sim.interestSaved, true)} saved` : ""}
            />
            <Row k="Prepayment charge" v={formatINR(sim.chargeAmt)} />
            <Row k="Net saving" v={formatINR(sim.netSaving, true)} highlight />
            <Row k="Closure date" v={sim.isClosed ? "Now" : sim.afterClosure} />
          </Card>
        </div>
      )}

      {bothCompare && (
        <div className="text-xs rounded-md border hairline p-3 bg-[hsl(var(--muted))]/20" data-testid="prepay-both-compare">
          Reduce tenure saves <b className="text-[hsl(var(--primary))]">{formatINR(Math.max(bothCompare.tenure_more, 0), true)}</b> more interest; Reduce EMI lowers your EMI by <b>{formatINR(bothCompare.emi_lower)}</b> per month.
        </div>
      )}

      {/* Chart */}
      {sim && <OutstandingChart before={sim.before} after={sim.after} prepays={expandPrepayments(lines)} />}

      {/* Action buttons */}
      <div className="flex gap-2 flex-wrap pt-2 border-t hairline">
        <input
          type="text" value={scenarioName}
          onChange={(e) => setScenarioName(e.target.value)}
          placeholder="Name this scenario (e.g. Diwali bonus plan)"
          className="flex-1 min-w-[200px] bg-background border hairline rounded-md px-3 py-2 text-sm"
          data-testid="prepay-sim-scenario-name"
        />
        <button onClick={saveScenario} className="btn-primary text-sm px-4 py-2 inline-flex items-center gap-1" data-testid="prepay-sim-save">
          <FloppyDisk size={13} /> Save scenario
        </button>
        <button onClick={applyToLoan} className="btn-ghost text-sm px-4 py-2 border hairline rounded-md inline-flex items-center gap-1" data-testid="prepay-sim-apply">
          Apply to loan
        </button>
        <button onClick={downloadExcel} className="btn-ghost text-sm px-4 py-2 border hairline rounded-md inline-flex items-center gap-1" data-testid="prepay-sim-xlsx">
          <FileXls size={13} /> Download Excel
        </button>
      </div>

      {/* Saved scenarios list */}
      {!!scenarios.length && (
        <div className="pt-3 border-t hairline" data-testid="prepay-sim-saved">
          <div className="text-xs text-muted-foreground uppercase tracking-wide mb-2">
            Saved scenarios ({scenarios.length}/10)
          </div>
          <div className="space-y-1">
            {scenarios.map((s) => (
              <div key={s.id} className={`flex items-center justify-between text-xs p-2 rounded border hairline ${s.stale ? "border-destructive/50 bg-destructive/5" : ""}`}>
                <div>
                  <span className="font-serif">{s.name}</span>
                  <span className="text-muted-foreground"> · {s.loan_name} · saved {formatINR(s.summary.net_saving, true)}</span>
                  {s.stale && (
                    <span className="ml-2 text-[10px] text-destructive" data-testid={`prepay-sim-stale-${s.id}`}>
                      · Rate changed — recalculate
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {s.stale && (
                    <button
                      onClick={() => {
                        const inp = s.inputs || {};
                        setLoanIdx((loans || []).findIndex((l) => l.name === s.loan_name) || 0);
                        if (Array.isArray(inp.lines) && inp.lines.length) setLines(inp.lines);
                        if (inp.reduceMode) setReduceMode(inp.reduceMode);
                        if (inp.stepUpPct !== undefined) setStepUpPct(inp.stepUpPct);
                        toast.info("Scenario loaded with the new rate — review and save again");
                      }}
                      className="text-[10px] underline text-[hsl(var(--secondary))]"
                      data-testid={`prepay-sim-recalc-${s.id}`}>
                      Recalculate
                    </button>
                  )}
                  <button onClick={() => deleteScenario(s.id)} className="text-muted-foreground hover:text-destructive" data-testid={`prepay-sim-del-${s.id}`}>
                    <Trash size={11} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="text-[11px] text-muted-foreground italic">
        Estimates for planning only. Not financial or tax advice. Please confirm exact figures with your lender.
      </div>
    </section>
  );
}

function Card({ title, children, highlight, testid }) {
  return (
    <div
      className={`rounded-md border hairline p-4 ${highlight ? "bg-[hsl(var(--primary))]/5 border-[hsl(var(--primary))]/40" : "bg-card"}`}
      data-testid={testid}
    >
      <div className="text-[10px] text-muted-foreground uppercase tracking-wide font-serif mb-2">{title}</div>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}
function Row({ k, v, delta, highlight }) {
  return (
    <div className="flex items-start justify-between gap-3 text-sm">
      <span className="text-muted-foreground text-xs">{k}</span>
      <div className="text-right">
        <div className={`font-mono ${highlight ? "font-semibold text-[hsl(var(--primary))]" : ""}`}>{v}</div>
        {delta && <div className="text-[10px] text-[hsl(var(--primary))]">{delta}</div>}
      </div>
    </div>
  );
}

function OutstandingChart({ before, after, prepays }) {
  const w = 640, h = 170, pad = 32;
  const months = Math.max(before.months, after.months, 1);
  const maxV = before.rows[0]?.opening_balance || 1;
  const x = (m) => pad + (m / months) * (w - pad * 2);
  const y = (v) => h - pad - (v / maxV) * (h - pad * 2);
  const toPath = (rows) =>
    rows.map((r, i) => `${i === 0 ? "M" : "L"}${x(r.month_no)},${y(r.closing_balance)}`).join(" ");
  return (
    <div className="border hairline rounded-md p-3 bg-card" data-testid="prepay-sim-chart">
      <div className="text-xs mb-1 flex items-center gap-3 flex-wrap">
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-3 h-0.5 bg-muted-foreground" /> Before
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-3 h-0.5 bg-[hsl(var(--primary))]" /> After
        </span>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-auto">
        <line x1={pad} y1={h - pad} x2={w - pad} y2={h - pad} stroke="currentColor" strokeWidth="0.5" opacity="0.3" />
        <line x1={pad} y1={pad} x2={pad} y2={h - pad} stroke="currentColor" strokeWidth="0.5" opacity="0.3" />
        <path d={toPath(before.rows)} fill="none" stroke="currentColor" strokeOpacity="0.5" strokeWidth="1.5" />
        <path d={toPath(after.rows)} fill="none" stroke="hsl(var(--primary))" strokeWidth="2" />
        {prepays.map((p) => {
          const row = after.rows.find((r) => r.month_no === p.month);
          if (!row || p.month > months) return null;
          return (
            <circle
              key={`pp-${p.month}`}
              cx={x(p.month)} cy={y(row.closing_balance)}
              r="2.5" fill="hsl(var(--primary))" stroke="white" strokeWidth="0.5"
            />
          );
        })}
      </svg>
    </div>
  );
}
